import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, type RouterStateSnapshot } from '@angular/router';
import { map, take } from 'rxjs';

import {
    MODULE_PAGES_MAP,
    type MODULES,
    PAGE_ROUTE_MAP,
    type PAGES,
} from '@app/shared/constants/shared.constant';
import type { NgActivatedRouteSnapshot } from '@app/shared/interface/ng-router.interface';
import { PermissionService } from '@app/shared/services/permission.service';
import { Helpers } from '@app/shared/utils/helpers';

/**
 * If the user does not have access to the defaultPage, take them to one of the other pages.
 * We handle the following cases:
 * Case 1: If default page is set to Enable, we open that
 * Case 2: If default page is not Enabled, we use the first enabled page
 * Case 3: If there is no tab permission, we redirect to unauthorized page
 * NOTE: This only considers the pages we create - not Shortcuts. This means, a user cannot
 * have only Shortcuts in a module.
 * @param route Route object
 */
export const defaultSubMenuPageGuard = (
    route: NgActivatedRouteSnapshot<{ defaultPage: PAGES; moduleName: MODULES }>,
    state: RouterStateSnapshot,
) => {
    const router = inject(Router);
    const permissionService = inject(PermissionService);

    return toObservable(permissionService.currentProjectPermissions).pipe(
        take(1),
        map(() => {
            // Case 1: If user can access the default page - continue
            if (
                route.data.defaultPage &&
                permissionService.canAccessModulePage(
                    Helpers.getPermissionName(route.data.moduleName, route.data.defaultPage),
                )
            ) {
                router.navigate([state.url, PAGE_ROUTE_MAP[route.data.defaultPage]]);

                return false;
            }

            // Case 2: Find the first page they have access to - and route them there
            const firstAccessiblePage = MODULE_PAGES_MAP[route.data.moduleName].find((x: PAGES) =>
                permissionService.canAccessModulePage(
                    Helpers.getPermissionName(route.data.moduleName, x),
                ),
            );
            if (firstAccessiblePage) {
                router.navigate([state.url, PAGE_ROUTE_MAP[firstAccessiblePage]]);

                return false;
            }

            // Case 3: No access in this module found - go to unauthorized
            router.navigate([permissionService.currentProject(), 'unauthorised'].filter(Boolean));

            return false;
        }),
    );
};
